// Native (Capacitor) bridge for the iOS/Android wrapper.
// On the web this is inert: isNative() is false and nothing here changes web behavior.
//
// Why this exists: Google blocks OAuth inside an app WebView, so on native we open sign-in in the
// system browser (SFSafariViewController / Custom Tab) and return to the app through a custom URL
// scheme (discern://). The returned session_id / result is then handed to the EXISTING web flow so
// the session cookie is set first-party inside the WebView (no cookie bridging needed).
import { Capacitor } from "@capacitor/core";
import { App } from "@capacitor/app";
import { Browser } from "@capacitor/browser";

const SCHEME = "discern";
let initialized = false;

export const isNative = () => {
  try {
    return Capacitor?.isNativePlatform?.() === true;
  } catch {
    return false;
  }
};

function parseAppUrl(url) {
  // discern://auth?session_id=...  or  discern://gmail?gmail=connected&email=...
  try {
    const rest = url.slice(`${SCHEME}://`.length);
    const [hostPart, queryPart = ""] = rest.split("?");
    const host = hostPart.replace(/\/+$/, "");
    const params = Object.fromEntries(new URLSearchParams(queryPart).entries());
    return { host, params };
  } catch {
    return { host: "", params: {} };
  }
}

/** Register the deep-link listener once. Call from the app root. No-op on web. */
export function initNativeBridge() {
  if (!isNative() || initialized) return;
  initialized = true;
  App.addListener("appUrlOpen", async ({ url }) => {
    if (!url || !url.startsWith(`${SCHEME}://`)) return;
    try {
      await Browser.close();
    } catch {
      /* browser may already be closed */
    }
    const { host, params } = parseAppUrl(url);
    if (host === "auth" && params.session_id) {
      // Let the existing AuthCallback exchange the session_id (first-party POST sets the cookie in the WebView).
      window.location.href = `/dashboard#session_id=${encodeURIComponent(params.session_id)}`;
    } else if (host === "gmail") {
      const q = new URLSearchParams(params).toString();
      window.location.href = `/settings?${q}`;
    }
  });
}

/** Sign in on native: open Emergent's hosted Google login in the system browser.
 * It redirects to /native-auth.html which bounces to discern://auth?session_id=... */
export async function nativeSignIn() {
  const redirect = `${window.location.origin}/native-auth.html`;
  const url = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  await Browser.open({ url, presentationStyle: "popover" });
}

/** Open any OAuth consent URL (e.g. Gmail connect) in the system browser. */
export async function nativeOpenOAuth(url) {
  await Browser.open({ url, presentationStyle: "popover" });
}

/**
 * Native Sign in with Apple (iOS ASAuthorization sheet). Returns the pieces our backend needs:
 * the id_token, the raw nonce (backend compares its SHA-256), and any name shared on first login.
 * Only call on native; the plugin is dynamically imported so web bundles never touch native code.
 */
export async function nativeAppleAuthorize() {
  const { SignInWithApple } = await import("@capacitor-community/apple-sign-in");
  const rand = (globalThis.crypto?.randomUUID?.() || Math.random().toString(36).slice(2));
  const nonce = `${rand}${Date.now().toString(36)}`;
  const result = await SignInWithApple.authorize({ scopes: "name email", nonce });
  const r = result?.response || {};
  const name = [r.givenName, r.familyName].filter(Boolean).join(" ").trim();
  return { id_token: r.identityToken, nonce, name };
}
